<style lang="scss" scoped>
.filters {
    .filter-holder {
        @apply border-light inline-flex bg-white rounded-sm shadow-lg flex-col lg:flex-row;
        @media (max-width: 1023.98px) {
            width: 100%;
        }

        .sector-filter {
            width: 100%;
            @media (max-width: 1023.98px) {
                border-bottom: 1px solid #f0f0f0;
            }
            @media (min-width: 1024px) {
                border-right: 1px solid #d8d8d8;
                width: 262px;
            }
        }

        .radius-filter {
            width: 100%;
            @media (max-width: 1023.98px) {
                border-bottom: 1px solid #f0f0f0;
            }
            @media (min-width: 1024px) {
                width: 120px;
            }
        }

        .places-filter {
            @apply relative;
            width: 100%;
            @media (max-width: 1023.98px) {
                padding: 20px 0;
                border-bottom: 1px solid #f0f0f0;
            }
            @media (min-width: 1024px) {
                width: 262px;
            }
            ::v-deep .vbga-input {
                @apply bg-white w-100 h-100 text-small font-semiBold pl-32 pr-24 py-16 border border-none focus:outline-none focus:ring-0 shadow-none transition-colors;
            }
            ::v-deep .vbga-results {
                @apply absolute bg-white p-16 rounded-sm;
                box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.04);
                padding: 24px;
                top: 70px;
                width: 100%;
                z-index: 10;
                @media (min-width: 1024px) {
                    width: 300px;
                    top: 100px;
                }
                li {
                    @apply flex p-8 text-dark font-mon hover:bg-light transition-colors cursor-pointer rounded-sm;
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 16px;
                    font-weight: 500;
                }
            }
        }
    }
}

.custom-dropdown {
    @apply relative;
    padding: 32px 26px;

    @media (min-width: 768px) {
        padding: 36px 33px;
    }

    .dropdown-input {
        @apply flex flex-row items-center cursor-pointer;
        .icon {
            margin-right: 9px;
            height: 18px;
            width: 18px;
            flex: none;
            ::v-deep svg {
                path {
                    @apply stroke-gray transition-colors;
                }
            }
        }
        .placeholder {
            @apply text-gray font-semiBold text-small transition-colors;
            letter-spacing: 0.1px;
            line-height: 18px;
            width: 100%;
        }
        .value {
            @apply text-dark font-semiBold text-small transition-colors;
            letter-spacing: 0.1px;
            line-height: 18px;
            width: 100%;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }
        .chevron {
            flex: none;
            ::v-deep svg path {
                @apply stroke-gray transition-colors;
            }
        }
        &:hover {
            ::v-deep .icon svg path,
            ::v-deep .chevron svg path {
                @apply stroke-dark;
            }
            .placeholder {
                @apply text-dark;
            }
        }
    }
    .radius-results {
        width: 100%;
        @media (min-width: 1024px) {
            width: 240px;
        }
    }
    .sector-results {
        height: 288px;
        width: 100%;
        @media (min-width: 1024px) {
            width: 263px;
        }
    }
    .search-results {
        @apply absolute rounded-sm bg-white;
        box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.04);
        padding: 24px;
        overflow-x: hidden;
        overflow-y: auto;
        top: 70px;
        left: 0;
        z-index: 1;
        @media (min-width: 1024px) {
            top: 100px;
        }
        .item {
            font-size: 14px;
            letter-spacing: 0;
            line-height: 16px;
            font-weight: 500;
            @apply flex p-8 text-dark font-mon hover:bg-light transition-colors cursor-pointer rounded-sm;
            &:before {
                content: "";
                height: 16px;
                border: 1px solid;
                width: 16px;
                margin-right: 12px;
                flex: none;
                @apply block rounded-sm transition-colors border-gray;
            }
            &:not(.checked):hover {
                &:before {
                    @apply border-dark;
                }
            }
            &.checked {
                &:before {
                    @apply bg-green border-green;
                }
            }
        }
    }
}
</style>

<template>
    <div class="filters">
        <div class="filter-holder">
            <!-- Sector dropodown -->
            <div class="sector-filter custom-dropdown" v-click-outside="() => (showSector = false)" @click="() => (showSector = true)">
                <div class="dropdown-input">
                    <span class="icon" v-html="svg('spyglass')"></span>
                    <div v-if="localValue.sector.length === 0" class="placeholder">Sector</div>
                    <div v-else class="value">
                        <template v-for="displaySector in localValue.sector">
                            <span :key="displaySector" v-html="sectors.find((sector) => sector.id === displaySector).name"></span>
                            ,
                        </template>
                    </div>
                    <span v-if="!showSector" class="chevron" v-html="svg('chevron-down')"></span>
                    <span v-else class="chevron" v-html="svg('Close')" @click.stop="() => (showSector = false)"></span>
                </div>
                <div v-show="showSector" class="sector-results search-results">
                    <div
                        class="item"
                        v-for="sector in sectors"
                        :key="sector.id"
                        :class="{ checked: localValue.sector.includes(sector.id) }"
                        @click="
                            $root.pushOrSlice(localValue.sector, sector.id);
                            onValueChanged();
                        "
                        v-html="sector.name"
                    ></div>
                </div>
            </div>

            <!-- City search -->
            <google-places-autocomplete :value="localValue.location" @resultChanged="onPlacesChanged" @resultCleared="onPlacesCleared" class="places-filter" placeholder="Gemeente of postcode">
                <div class="h-100" slot="input" slot-scope="{ context, events, actions }">
                    <input
                        v-model="context.input"
                        @focus="events.inputHasReceivedFocus"
                        @input="events.inputHasChanged"
                        @keydown.enter.prevent="actions.selectItemFromList"
                        @keydown.down.prevent="actions.shiftResultsSelection"
                        @keydown.up.prevent="actions.unshiftResultsSelection"
                        type="search"
                        id="locationInput"
                        class="vbga-input"
                        placeholder="Gemeente of postcode"
                    />
                </div>
            </google-places-autocomplete>

            <!-- Radius dropodown -->
            <div class="radius-filter custom-dropdown" v-click-outside="() => (showRadius = false)" @click="() => (showRadius = true)">
                <div class="dropdown-input">
                    <div v-if="!localValue.rad" class="placeholder">Straal</div>
                    <div v-else class="value">{{ localValue.rad }}km</div>
                    <span v-if="!showRadius" class="chevron" v-html="svg('chevron-down')"></span>
                    <span v-else class="chevron" v-html="svg('Close')" @click.stop="() => (showRadius = false)"></span>
                </div>
                <div v-show="showRadius" class="radius-results search-results">
                    <label for="radiusslider" class="text-small font-mon font-semiBold mb-12">{{ localValue.rad }}km</label>
                    <vue-slider
                        id="radiusslider"
                        v-model="localValue.rad"
                        tooltip="none"
                        :dot-size="18"
                        :max="50"
                        :interval="5"
                        :dot-style="{ backgroundColor: '#fff', borderColor: '#00B519', boxShadow: 'none' }"
                        :process-style="{ backgroundColor: '#00B519' }"
                        :rail-style="{ backgroundColor: '#C8D4DE' }"
                    />
                </div>
            </div>

            <button class="lg:text-left bg-green border-green font-semiBold text-p lg:flex hover:bg-white hover:text-green items-center block px-40 py-16 m-16 leading-relaxed text-center text-white transition-colors border rounded-sm" @click="onSubmit">
                Zoeken
                <el-loader :loading="searching" class="ml-8"></el-loader>
            </button>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import GooglePlacesAutocomplete from "../libraries/GooglePlacesAutocomplete.vue";
import ClickOutside from "vue-click-outside";
import VueSlider from "vue-slider-component";

export default {
    components: {
        vSelect,
        VueSlider,
        GooglePlacesAutocomplete,
    },

    directives: {
        ClickOutside,
    },

    props: {
        value: {
            required: false,
            type: Object,
            default: () => ({
                sector: [],
                rad: null,
                lat: null,
                long: null,
                location: null,
            }),
        },
        redirect: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        localValue: {
            sector: [],
            rad: null,
            lat: null,
            long: null,
            location: null,
        },
        sectors: [],

        searching: false,
        loading: false,
        success: false,
        showSector: false,
        showRadius: false,
    }),

    mounted() {
        this.getSectors();
    },

    methods: {
        getSectors() {
            this.loading = true;
            this.success = false;
            axios
                .get("/api/sectors?used=true")
                .then((response) => this.handleFetch(response.data))
                .catch((error) => this.handleError(error))
                .finally(() => (this.loading = false));
        },
        handleFetch(data) {
            this.sectors = data;
            this.success = true;
        },
        handleError(e) {
            console.error(e);
        },
        onValueChanged() {
            this.$emit("input", this.localValue);
        },
        onSubmit() {
            if (!this.redirect) {
                this.setQueryParams();
                this.searching = true;
                this.$emit("submit", this.localValue);
            } else {
                window.location = "/jobs?" + this.serialize(this.localValue);
            }
        },
        setQueryParams() {
            this.serialize(this.localValue);
        },
        onPlacesChanged(val) {
            this.localValue.lat = val.geometry.location.lat();
            this.localValue.long = val.geometry.location.lng();
            this.localValue.location = val.formatted_address;
            this.onValueChanged();
        },
        onPlacesCleared() {
            this.localValue.lat = null;
            this.localValue.long = null;
            this.localValue.location = null;
            this.localValue.rad = null;
        },
        serialize(obj) {
            var str = [];
            for (var p in obj)
                if (obj.hasOwnProperty(p)) {
                    str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
                }
            return str.join("&");
        },
    },

    watch: {
        value: {
            immediate: true,
            handler: function (val) {
                this.localValue = val;
            },
        },
    },
};
</script>
